import { useEffect, useState } from 'react';
import { apiBaseUrl } from '../../api/runtime-config';
import { cx } from '../cx';
import styles from './Avatar.module.css';

export interface AvatarProps {
  name: string;
  /**
   * The person's user id. When given, their Microsoft 365 / Entra profile
   * photo (synced by the server) is shown over the initials once it loads;
   * people without a photo keep their initials.
   */
  userId?: string | null | undefined;
  /** sm 24px · compact 28px (dense table rows) · md 32px · lg 40px. */
  size?: 'sm' | 'compact' | 'md' | 'lg';
  className?: string;
  /** The name is already visible next to the avatar: hide it from assistive tech so it isn't announced twice. */
  decorative?: boolean;
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/** Deterministic hue from the name so a person's avatar color is stable across sessions without storing one. */
function hueFor(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(hash) % 360;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Per page-load memory of who has no photo, so a list re-render does not
 * mount (and fade) an image that is known to be the empty placeholder.
 * Bounded; the browser's HTTP cache does the rest.
 */
const knownWithoutPhoto = new Set<string>();
const MAX_REMEMBERED = 5000;

/** Test hook. */
export function __resetAvatarPhotoMemoryForTests(): void {
  knownWithoutPhoto.clear();
}

export function avatarPhotoUrl(userId: string): string {
  return `${apiBaseUrl()}/users/${encodeURIComponent(userId)}/photo`;
}

/**
 * The photo layer of an avatar: an <img> absolutely positioned over the
 * initials (the parent must be `position: relative; overflow: hidden` and
 * round). Invisible until it has loaded a real picture. The server answers
 * "no photo" with a 1x1 transparent PNG, so a 1-pixel image — or any load
 * error — leaves the initials showing. Always decorative (alt="").
 */
export function AvatarPhoto({ userId, className }: { userId: string | null | undefined; className?: string }) {
  const usable = Boolean(userId && UUID_RE.test(userId) && !knownWithoutPhoto.has(userId.toLowerCase()));
  const [state, setState] = useState<'loading' | 'shown' | 'none'>('loading');
  useEffect(() => setState('loading'), [userId]);
  if (!usable || state === 'none' || !userId) return null;

  const markNone = () => {
    if (knownWithoutPhoto.size < MAX_REMEMBERED) knownWithoutPhoto.add(userId.toLowerCase());
    setState('none');
  };
  return (
    <img
      src={avatarPhotoUrl(userId)}
      alt=""
      aria-hidden="true"
      draggable={false}
      loading="lazy"
      decoding="async"
      className={cx(styles.photo, state === 'shown' && styles.photoShown, className)}
      onLoad={(e) => (e.currentTarget.naturalWidth > 1 ? setState('shown') : markNone())}
      onError={markNone}
    />
  );
}

/** Initials avatar, with the person's profile photo layered on top when `userId` is given and they have one. */
export function Avatar({ name, userId, size = 'md', className, decorative = false }: AvatarProps) {
  const style = { '--avatar-hue': hueFor(name) } as React.CSSProperties;
  if (decorative) {
    return (
      <span className={cx(styles.avatar, styles[size], className)} style={style} aria-hidden="true">
        {initialsFor(name)}
        <AvatarPhoto userId={userId} />
      </span>
    );
  }
  return (
    <span className={cx(styles.avatar, styles[size], className)} style={style} title={name}>
      <span aria-hidden="true">{initialsFor(name)}</span>
      <span className={styles.srOnly}>{name}</span>
      <AvatarPhoto userId={userId} />
    </span>
  );
}
